import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  HardDrive, Cloud, Trash2, Download, RefreshCw, ArrowUpCircle,
  AlertTriangle, CheckCircle2, Film, Image as ImageIcon, Video, Loader2, GitPullRequestArrow, Sparkles
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const fileTypeLabel = (key) => {
  const filename = key.split('/').pop() || '';
  const base = filename.split('.')[0].toLowerCase();
  const ext = filename.split('.').pop().toLowerCase();
  if (ext === 'mp4' || ext === 'mov' || ext === 'webm' || ext === 'm4v') {
    if (base.startsWith('trailer')) return { label: 'Trailer', color: '#F59E0B' };
    return { label: 'Video', color: '#EF4444' };
  }
  if (ext === 'manifest') return { label: 'Manifest', color: '#8B5CF6' };
  if (base.includes('part_')) return { label: 'Part', color: '#6B7280' };
  if (base === 'poster_desktop' || base === 'banner') return { label: 'Banner', color: '#3B82F6' };
  if (base === 'poster_mobile' || base === 'poster') return { label: 'Poster', color: '#10B981' };
  if (ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'webp' || ext === 'gif') {
    if (key.includes('/avatars/')) return { label: 'Avatar', color: '#EC4899' };
    return { label: 'Image', color: '#10B981' };
  }
  return { label: ext.toUpperCase() || 'File', color: '#8197A4' };
};

const iconForKey = (key) => {
  const k = key.toLowerCase();
  if (k.endsWith('.mp4') || k.endsWith('.mov') || k.endsWith('.webm') || k.endsWith('.m4v')) return <Video size={14} />;
  if (k.endsWith('.png') || k.endsWith('.jpg') || k.endsWith('.jpeg') || k.endsWith('.webp')) return <ImageIcon size={14} />;
  return <Film size={14} />;
};

// Human-readable "N unit ago" for an ISO timestamp. Returns null if the input
// is falsy or unparseable so callers can decide how to render a missing value.
const formatTimeAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) return 'just now';
  if (secs < 90) return '1 minute ago';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
};

export const StorageManagerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [migration, setMigration] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [legacy, setLegacy] = useState(null);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [legacyDeleting, setLegacyDeleting] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const [reorg, setReorg] = useState(null);
  const [reorganizing, setReorganizing] = useState(false);
  // "Sync from Deployed" — pulls the movie catalog from the production URL
  // into the local Mongo. Read initial URL from env (Emergent sets FRONTEND_URL
  // in backend/.env but the frontend can only guess based on convention).
  const defaultDeployedUrl = (() => {
    const previewHost = window.location.host;
    // preview URLs look like <slug>-<n>.preview.emergentagent.com
    // deployed URLs look like <slug>-<n>.emergent.host
    const m = previewHost.match(/^(.+?)\.preview\.emergentagent\.com$/);
    return m ? `https://${m[1]}.emergent.host` : '';
  })();
  const [syncUrl, setSyncUrl] = useState(defaultDeployedUrl);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null); // { last_synced_at, synced_count }
  const [mirrorMode, setMirrorMode] = useState(false);
  const [purging, setPurging] = useState(false);
  const [tmdbFetching, setTmdbFetching] = useState(false);
  const [tmdbResult, setTmdbResult] = useState(null);
  const [tmdbOverwrite, setTmdbOverwrite] = useState(false);

  const fetchSyncStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/storage/sync-from-deployed/status`, { withCredentials: true });
      setSyncStatus(data);
    } catch (e) { /* soft-fail — status is optional */ }
  };

  useEffect(() => {
    if (!user || user.role !== 'master_admin') {
      navigate('/');
      return;
    }
    refresh();
    pollMigration();
    fetchSyncStatus();
  }, [user, navigate]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [h, l, leg, al, ro] = await Promise.all([
        axios.get(`${API}/admin/r2/health`, { withCredentials: true }),
        axios.get(`${API}/admin/storage/list`, { withCredentials: true }),
        axios.get(`${API}/admin/storage/emergent-legacy`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/storage/alerts`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/storage/reorganize-status`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setHealth(h.data);
      setListing(l.data);
      setLegacy(leg.data);
      setAlerts(al.data);
      setReorg(ro.data);
      if (ro.data && ro.data.status === 'running') pollReorganize();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load storage data');
    } finally {
      setLoading(false);
    }
  };

  const pollReorganize = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/storage/reorganize-status`, { withCredentials: true });
      setReorg(data);
      if (data.status === 'running') {
        setReorganizing(true);
        setTimeout(pollReorganize, 3000);
      } else {
        setReorganizing(false);
        if (data.status === 'done') refresh();
      }
    } catch (_) { /* silent */ }
  };

  const startReorganize = async () => {
    if (!window.confirm('Rename all R2 files into title-slug folders (e.g. streamshelf/movies/melvyn-fd260d49/…)?\n\nThis makes files easier to identify at a glance in the Cloudflare R2 dashboard. Safe to run any time — already-correctly-named files are skipped.')) return;
    setReorganizing(true);
    try {
      await axios.post(`${API}/admin/storage/reorganize`, {}, { withCredentials: true });
      toast.success('Reorganize started');
      pollReorganize();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start reorganize');
      setReorganizing(false);
    }
  };

  const pollMigration = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/storage/migrate-status`, { withCredentials: true });
      setMigration(data);
      if (data.status === 'running') {
        setMigrating(true);
        setTimeout(pollMigration, 3000);
      } else {
        setMigrating(false);
      }
    } catch (_) { /* silent */ }
  };

  const startMigration = async () => {
    if (!window.confirm('Migrate all Emergent-hosted files to Cloudflare R2? Non-reversible without a fresh copy. Old files stay in Emergent until you delete them manually.')) return;
    setMigrating(true);
    try {
      await axios.post(`${API}/admin/storage/migrate`, {}, { withCredentials: true });
      toast.success('Migration started');
      pollMigration();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start migration');
      setMigrating(false);
    }
  };

  const runTmdbFetch = async (dryRun) => {
    if (!dryRun && !window.confirm(
      `Fetch posters from TMDB for ${tmdbOverwrite ? 'ALL movies (overwriting existing artwork)' : 'movies without posters'}?\n\n` +
      `• Adds official poster + backdrop URLs from TMDB's public CDN.\n` +
      `• Free public data — no upload to R2, no storage cost.\n` +
      `• Only matches with correct year (±1) are applied.`
    )) return;
    setTmdbFetching(true);
    setTmdbResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/movies/auto-fetch-posters`,
        { dry_run: dryRun, overwrite: tmdbOverwrite },
        { withCredentials: true }
      );
      setTmdbResult(data);
      toast.success(dryRun
        ? `Dry run: ${data.matched} matched · ${data.no_match} unmatched`
        : `${data.updated} posters attached · ${data.no_match} unmatched`);
      if (!dryRun) refresh();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === 'string' ? detail : 'TMDB fetch failed');
    } finally {
      setTmdbFetching(false);
    }
  };


  const runPurgeLegacy = async (dryRun) => {
    if (!dryRun && !window.confirm(
      'Purge Emergent-legacy references from every movie?\n\n' +
      '• For each stale field: if a matching R2 file exists, rewrite the path to point at R2. Otherwise, clear the field so the admin can re-upload.\n' +
      '• Never deletes R2 files.\n' +
      '• Safe to re-run.'
    )) return;
    setPurging(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/storage/purge-emergent-legacy${dryRun ? '?dry_run=true' : ''}`,
        {},
        { withCredentials: true }
      );
      const summary = `${data.rewritten_to_r2} rewritten · ${data.cleared} cleared · ${data.movies_touched} movies affected`;
      toast.success(dryRun ? `Dry run: ${summary}` : `Purge complete: ${summary}`);
      if (!dryRun) refresh();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === 'string' ? detail : 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const runSyncFromDeployed = async (dryRun) => {
    if (!syncUrl || !/^https?:\/\//.test(syncUrl)) {
      toast.error('Enter a valid deployed URL (must start with https://)');
      return;
    }
    if (!dryRun && !window.confirm(
      `Pull the entire movie catalog from ${syncUrl} into this preview environment?\n\n` +
      `• One-way: deployed → preview. Deployed data is NOT modified.\n` +
      `• Upserts by movie_id — safe to re-run.\n` +
      `• Users, borrows, and watch history are NOT synced.` +
      (mirrorMode
        ? `\n\n⚠ MIRROR MODE is ON. Any movie in preview that does NOT exist on deployed will be DELETED (from Mongo only — R2 files are untouched). Related borrows/reservations/likes for those movies will also be cleaned up.`
        : '')
    )) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/storage/sync-from-deployed`,
        { deployed_url: syncUrl, dry_run: dryRun, mirror_mode: mirrorMode },
        { withCredentials: true }
      );
      setSyncResult(data);
      const bits = [`${data.inserted} inserted`, `${data.updated} updated`];
      if (data.mirror_mode) bits.push(`${data.deleted} deleted`);
      if (data.skipped) bits.push(`${data.skipped} skipped`);
      const summary = bits.join(' · ');
      toast.success(dryRun ? `Dry run: ${summary}` : `Sync complete: ${summary}`);
      if (!dryRun) {
        refresh();
        fetchSyncStatus();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === 'string' ? detail : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const downloadObject = async (key) => {
    setBusyKey(key);
    try {
      const { data } = await axios.get(`${API}/admin/storage/download-url`, { params: { key }, withCredentials: true });
      window.open(data.url, '_blank');
    } catch (err) {
      toast.error('Failed to generate download URL');
    } finally {
      setBusyKey(null);
    }
  };

  const deleteObject = async (key) => {
    if (!window.confirm(`Delete this object from R2?\n\n${key}\n\nThis cannot be undone.`)) return;
    setBusyKey(key);
    try {
      await axios.delete(`${API}/admin/storage/object`, { params: { key }, withCredentials: true });
      toast.success('Deleted');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyKey(null);
    }
  };

  const deleteLegacy = async () => {
    if (!window.confirm(
      `Attempt to delete ${legacy?.orphaned_count || 0} orphaned files (${legacy?.orphaned_size_human || '?'}) from Emergent Storage?\n\n` +
      `NOTE: Emergent Storage does not currently support deletion (their API returns 405). ` +
      `This request will be sent but is expected to fail with a clear error. ` +
      `If/when Emergent adds delete support, this button will start working automatically — no code change needed.`
    )) return;
    setLegacyDeleting(true);
    try {
      const { data } = await axios.delete(
        `${API}/admin/storage/emergent-legacy?orphaned_only=true`,
        { withCredentials: true }
      );
      if (data.deleted > 0) {
        toast.success(`Deleted ${data.deleted} legacy file${data.deleted === 1 ? '' : 's'} from Emergent Storage`);
      } else {
        toast.info(data.note || 'Emergent Storage does not support DELETE — no files removed');
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Legacy cleanup failed');
    } finally {
      setLegacyDeleting(false);
    }
  };

  const restoreOrphan = async (folder, title) => {
    if (!window.confirm(`Restore "${title}" as a new movie?\n\nThis creates a new movie record using the folder name as the title, and links the existing R2 files (video/poster/banner) to it. You can edit the metadata (genre, cast, synopsis, etc.) after restoration.`)) return;
    setBusyKey(folder);
    try {
      const { data } = await axios.post(`${API}/admin/storage/restore-orphan`, { folder }, { withCredentials: true });
      toast.success(data.message);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setBusyKey(null);
    }
  };


  const deletePrefix = async (prefix, title) => {
    if (!window.confirm(`Delete ALL files for "${title}"?\n\nPrefix: ${prefix}\n\nThis removes every object under that prefix from R2. Cannot be undone.`)) return;
    setBusyKey(prefix);
    try {
      const { data } = await axios.delete(`${API}/admin/storage/prefix`, { params: { prefix }, withCredentials: true });
      toast.success(data.message);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBusyKey(null);
    }
  };

  const inputStyle = { backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <Loader2 className="animate-spin" size={32} color="#3B82F6" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6" style={{ backgroundColor: '#0F171E' }} data-testid="storage-manager-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3" style={{ color: '#FFFFFF' }}>
              <HardDrive size={32} color="#3B82F6" />
              Storage Manager
            </h1>
            <p className="mt-2 text-sm sm:text-base leading-relaxed" style={{ color: '#8197A4' }}>
              Browse and manage every file on Cloudflare R2 — including orphaned files with no movie in the database.
            </p>
          </div>
          <button onClick={refresh}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold"
            style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3E5468' }}
            data-testid="refresh-storage">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>

        {/* R2 Health */}
        <div className="p-5 rounded-xl mb-6 flex items-center gap-4 flex-wrap"
          style={{ backgroundColor: '#1A2632', border: `1px solid ${health?.reachable ? '#10B981' : '#EF4444'}` }}
          data-testid="r2-health-card">
          <Cloud size={28} color={health?.reachable ? '#10B981' : '#EF4444'} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold" style={{ color: '#FFFFFF' }}>
              Cloudflare R2 — {health?.reachable ? 'Connected' : 'Unreachable'}
            </div>
            <div className="text-sm" style={{ color: '#8197A4' }}>
              Bucket: <span style={{ color: '#DDDDDD' }}>{health?.bucket || '—'}</span>
              {' · '}Used: <span style={{ color: '#DDDDDD' }}>{health?.used_human || '—'}</span>
              {' · '}Visibility: <span style={{ color: '#DDDDDD' }}>{health?.public ? 'Public' : 'Private (signed URLs)'}</span>
            </div>
            {health?.error && <div className="text-xs mt-1" style={{ color: '#EF4444' }}>{health.error}</div>}
          </div>
        </div>

        {/* Storage Health Alerts */}
        {alerts && (alerts.alerts?.length > 0 || alerts.r2) && (
          <div className="mb-6 p-5 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="storage-health-widget">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
              <div className="flex items-center gap-3">
                <HardDrive size={22} color={(alerts.alerts?.length || 0) > 0 ? '#F0C14B' : '#10B981'} />
                <div>
                  <h3 className="font-bold" style={{ color: '#FFFFFF' }}>
                    Storage Health {(alerts.alerts?.length || 0) === 0 && <span className="text-xs ml-1" style={{ color: '#10B981' }}>All clear</span>}
                  </h3>
                  <div className="text-xs" style={{ color: '#8197A4' }}>
                    R2: {alerts.r2?.used_human} of {alerts.r2?.quota_human} · {(alerts.r2?.pct || 0).toFixed(1)}% used
                  </div>
                </div>
              </div>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#2A3F52' }}>
              <div className="h-full transition-all" style={{
                width: `${Math.max(1, Math.min(100, alerts.r2?.pct || 0))}%`,
                backgroundColor: (alerts.r2?.pct || 0) >= 95 ? '#EF4444' : (alerts.r2?.pct || 0) >= 80 ? '#F0C14B' : (alerts.r2?.pct || 0) >= 60 ? '#3B82F6' : '#10B981',
              }} />
            </div>
            {(alerts.alerts || []).map((a, i) => {
              const color = a.severity === 'critical' ? '#EF4444' : a.severity === 'warning' ? '#F0C14B' : '#3B82F6';
              const isMigrationStale = a.kind === 'migration_stale';
              return (
                <div key={i} className="mt-3 p-2.5 rounded-lg flex items-start gap-2 text-xs"
                  style={{ backgroundColor: `${color}12`, border: `1px solid ${color}55` }}
                  data-testid={`sm-alert-${a.kind}`}>
                  <AlertTriangle size={14} color={color} className="mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-semibold" style={{ color: '#FFFFFF' }}>{a.title}</div>
                    <div style={{ color: '#8197A4' }}>{a.message}</div>
                    {isMigrationStale && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => runPurgeLegacy(true)}
                          disabled={purging}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold transition-all disabled:opacity-60"
                          style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: `1px solid ${color}` }}
                          data-testid="purge-legacy-dry-run"
                          title="Preview what will change without writing"
                        >
                          {purging ? <Loader2 className="animate-spin" size={11} /> : <CheckCircle2 size={11} />}
                          Dry run
                        </button>
                        <button
                          onClick={() => runPurgeLegacy(false)}
                          disabled={purging}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold transition-all disabled:opacity-60"
                          style={{ backgroundColor: color, color: '#0F171E' }}
                          data-testid="purge-legacy-run"
                        >
                          {purging ? <Loader2 className="animate-spin" size={11} /> : <Trash2 size={11} />}
                          {purging ? 'Purging…' : 'Purge legacy references'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Auto-fetch posters from TMDB — restores artwork for movies with missing thumbnails */}
        <div className="p-5 rounded-xl mb-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="tmdb-fetch-card">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: '#FFFFFF' }}>
                <Sparkles size={20} color="#F0C14B" />
                Fetch posters from TMDB
              </h3>
              <p className="text-sm mt-1" style={{ color: '#8197A4' }}>
                Auto-attach the official poster + backdrop image for every movie by matching its title + release year against TMDB&apos;s public database. Runs in seconds. No R2 upload needed — images served straight from TMDB&apos;s CDN.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => runTmdbFetch(true)}
              disabled={tmdbFetching}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
              style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #F0C14B' }}
              data-testid="tmdb-dry-run"
              title="Preview matches without writing"
            >
              {tmdbFetching ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
              Dry run
            </button>
            <button
              onClick={() => runTmdbFetch(false)}
              disabled={tmdbFetching}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
              style={{ backgroundColor: '#F0C14B', color: '#0F171E' }}
              data-testid="tmdb-run"
            >
              {tmdbFetching ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
              {tmdbFetching ? 'Fetching…' : 'Fetch posters'}
            </button>
          </div>

          <label
            className="mt-3 flex items-start gap-2 cursor-pointer select-none"
            data-testid="tmdb-overwrite-toggle"
          >
            <input
              type="checkbox"
              checked={tmdbOverwrite}
              onChange={(e) => setTmdbOverwrite(e.target.checked)}
              disabled={tmdbFetching}
              className="mt-1"
              style={{ accentColor: '#F0C14B' }}
            />
            <span className="text-xs" style={{ color: tmdbOverwrite ? '#F0C14B' : '#8197A4' }}>
              <span className="font-semibold">Overwrite existing artwork</span> — replace posters even for movies that already have one. Leave off to only fill in missing artwork.
            </span>
          </label>

          {tmdbResult && (
            <div className="mt-4 p-3 rounded-lg text-sm" style={{ backgroundColor: '#0F171E', border: `1px solid ${tmdbResult.dry_run ? '#F0C14B' : '#10B981'}` }} data-testid="tmdb-result">
              <div className="flex items-center gap-2 mb-2" style={{ color: tmdbResult.dry_run ? '#F0C14B' : '#10B981' }}>
                <CheckCircle2 size={16} />
                <span className="font-semibold">{tmdbResult.message}</span>
                <span className="text-xs" style={{ color: '#8197A4' }}>· considered {tmdbResult.considered} movies</span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xl font-bold" style={{ color: '#10B981' }}>{tmdbResult.matched}</div>
                  <div className="text-xs" style={{ color: '#8197A4' }}>Matched</div>
                </div>
                <div>
                  <div className="text-xl font-bold" style={{ color: '#EF4444' }}>{tmdbResult.no_match}</div>
                  <div className="text-xs" style={{ color: '#8197A4' }}>No match</div>
                </div>
                <div>
                  <div className="text-xl font-bold" style={{ color: '#8197A4' }}>{tmdbResult.skipped}</div>
                  <div className="text-xs" style={{ color: '#8197A4' }}>Skipped</div>
                </div>
              </div>
              {tmdbResult.results && tmdbResult.results.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs" style={{ color: '#8197A4' }}>Show per-movie details</summary>
                  <div className="mt-2 max-h-56 overflow-y-auto text-[11px] space-y-1">
                    {tmdbResult.results.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span style={{ color: r.status === 'matched' ? '#10B981' : '#EF4444' }}>
                          {r.status === 'matched' ? '✓' : '·'}
                        </span>
                        <span style={{ color: '#FFFFFF' }}>{r.title}</span>
                        {r.year && <span style={{ color: '#8197A4' }}>({r.year})</span>}
                        {r.matched_title && (
                          <>
                            <span style={{ color: '#8197A4' }}>→</span>
                            <span style={{ color: '#F0C14B' }}>{r.matched_title} ({r.matched_year})</span>
                          </>
                        )}
                        {r.status !== 'matched' && <span style={{ color: '#EF4444' }}>({r.status})</span>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Sync from Deployed panel — pulls the deployed catalog into preview */}
        <div className="p-5 rounded-xl mb-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="sync-from-deployed-card">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: '#FFFFFF' }}>
                <GitPullRequestArrow size={20} color="#3B82F6" />
                Sync Catalog from Deployed
              </h3>
              <p className="text-sm mt-1" style={{ color: '#8197A4' }}>
                Mirror the movie records from your deployed app into this preview environment. R2 assets (posters, videos, subtitles) are already shared, so thumbnails will render instantly. One-way: deployed → preview. Safe to re-run.
              </p>
            </div>
            {/* Freshness indicator — shows how long ago preview was last synced */}
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs whitespace-nowrap"
              style={{
                backgroundColor: syncStatus?.last_synced_at ? 'rgba(16, 185, 129, 0.12)' : 'rgba(129, 151, 164, 0.12)',
                border: `1px solid ${syncStatus?.last_synced_at ? '#10B981' : '#2A3F52'}`,
                color: syncStatus?.last_synced_at ? '#10B981' : '#8197A4',
              }}
              title={syncStatus?.last_synced_at ? `Exact: ${syncStatus.last_synced_at}` : 'This preview has not been synced yet'}
              data-testid="sync-last-synced"
            >
              <RefreshCw size={12} />
              {syncStatus?.last_synced_at
                ? <>Last synced: <span className="font-semibold">{formatTimeAgo(syncStatus.last_synced_at)}</span> · {syncStatus.synced_count} {syncStatus.synced_count === 1 ? 'movie' : 'movies'}</>
                : <>Never synced</>}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="url"
              value={syncUrl}
              onChange={(e) => setSyncUrl(e.target.value)}
              placeholder="https://your-app.emergent.host"
              className="flex-1 min-w-[220px] px-3 py-2 rounded-lg outline-none text-sm"
              style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' }}
              disabled={syncing}
              data-testid="sync-deployed-url-input"
            />
            <button
              onClick={() => runSyncFromDeployed(true)}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
              style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3B82F6' }}
              data-testid="sync-deployed-dry-run"
              title="Preview what will change without writing"
            >
              {syncing ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
              Dry run
            </button>
            <button
              onClick={() => runSyncFromDeployed(false)}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
              style={{ backgroundColor: '#3B82F6', color: '#FFFFFF' }}
              data-testid="sync-deployed-run"
            >
              {syncing ? <Loader2 className="animate-spin" size={14} /> : <GitPullRequestArrow size={14} />}
              {syncing ? 'Syncing…' : 'Sync now'}
            </button>
          </div>

          {/* Mirror mode toggle — makes preview a strict 1:1 copy of deployed */}
          <label
            className="mt-3 flex items-start gap-2 cursor-pointer select-none"
            data-testid="sync-mirror-mode-toggle"
          >
            <input
              type="checkbox"
              checked={mirrorMode}
              onChange={(e) => setMirrorMode(e.target.checked)}
              disabled={syncing}
              className="mt-1"
              style={{ accentColor: '#EF4444' }}
              data-testid="sync-mirror-mode-checkbox"
            />
            <span className="text-xs" style={{ color: mirrorMode ? '#EF4444' : '#8197A4' }}>
              <span className="font-semibold">Mirror mode</span> — also delete local-only movies (records not present on deployed). Removes duplicates from old seed data. R2 files are never touched.
            </span>
          </label>

          {syncResult && (
            <div className="mt-4 p-3 rounded-lg text-sm" style={{ backgroundColor: '#0F171E', border: `1px solid ${syncResult.dry_run ? '#3B82F6' : '#10B981'}` }} data-testid="sync-deployed-result">
              <div className="flex items-center gap-2 mb-2" style={{ color: syncResult.dry_run ? '#3B82F6' : '#10B981' }}>
                <CheckCircle2 size={16} />
                <span className="font-semibold">{syncResult.message}</span>
                <span className="text-xs" style={{ color: '#8197A4' }}>
                  as {syncResult.as_user} · fetched {syncResult.movies_fetched} movies
                </span>
              </div>
              <div className={`grid gap-3 text-center ${syncResult.mirror_mode ? 'grid-cols-4' : 'grid-cols-3'}`}>
                <div>
                  <div className="text-xl font-bold" style={{ color: '#10B981' }}>{syncResult.inserted}</div>
                  <div className="text-xs" style={{ color: '#8197A4' }}>New in preview</div>
                </div>
                <div>
                  <div className="text-xl font-bold" style={{ color: '#3B82F6' }}>{syncResult.updated}</div>
                  <div className="text-xs" style={{ color: '#8197A4' }}>Refreshed</div>
                </div>
                {syncResult.mirror_mode && (
                  <div>
                    <div className="text-xl font-bold" style={{ color: '#EF4444' }}>{syncResult.deleted}</div>
                    <div className="text-xs" style={{ color: '#8197A4' }}>{syncResult.dry_run ? 'Would delete' : 'Deleted'}</div>
                  </div>
                )}
                <div>
                  <div className="text-xl font-bold" style={{ color: '#8197A4' }}>{syncResult.skipped}</div>
                  <div className="text-xs" style={{ color: '#8197A4' }}>Skipped</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Migration Panel */}
        <div className="p-5 rounded-xl mb-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="migration-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: '#FFFFFF' }}>
                <ArrowUpCircle size={20} color="#F0C14B" />
                Emergent → Cloudflare R2 Migration
              </h3>
              <p className="text-sm mt-1" style={{ color: '#8197A4' }}>
                One-click migration of every remaining Emergent-hosted file (posters, banners, videos, multipart manifests) into R2. Safe to re-run — already-migrated files are skipped.
              </p>
            </div>
            <button onClick={startMigration} disabled={migrating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all disabled:opacity-60"
              style={{ backgroundColor: '#F0C14B', color: '#0F171E' }}
              data-testid="start-migration">
              {migrating ? <Loader2 className="animate-spin" size={16} /> : <ArrowUpCircle size={16} />}
              {migrating ? 'Running…' : 'Migrate Everything'}
            </button>
          </div>

          {migration && migration.status !== 'idle' && (
            <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52' }}>
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: '#DDDDDD' }}>
                  {migration.status === 'done'
                    ? <><CheckCircle2 className="inline mr-1" size={14} color="#10B981" /> Complete</>
                    : migration.status === 'failed'
                    ? <><AlertTriangle className="inline mr-1" size={14} color="#EF4444" /> Failed</>
                    : <><Loader2 className="inline mr-1 animate-spin" size={14} color="#3B82F6" /> {migration.status}</>}
                </span>
                <span style={{ color: '#8197A4' }}>
                  {migration.processed || 0} / {migration.total || 0} · migrated {migration.migrated || 0} · failed {migration.failed || 0}
                </span>
              </div>
              {migration.total > 0 && (
                <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#2A3F52' }}>
                  <div className="h-full transition-all" style={{
                    width: `${Math.round((migration.processed / migration.total) * 100)}%`,
                    background: 'linear-gradient(90deg, #3B82F6, #10B981)'
                  }} />
                </div>
              )}
              {migration.errors && migration.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer" style={{ color: '#EF4444' }}>{migration.errors.length} errors</summary>
                  <div className="mt-2 space-y-1">
                    {migration.errors.slice(0, 5).map((e, i) => (
                      <div key={i} className="text-xs" style={{ color: '#8197A4' }}>
                        <span style={{ color: '#EF4444' }}>{e.movie_id}/{e.field}:</span> {e.error}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {migration.log && migration.log.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer" style={{ color: '#8197A4' }}>Recent log</summary>
                  <div className="mt-2 space-y-0.5 max-h-32 overflow-y-auto">
                    {migration.log.slice(-10).map((line, i) => (
                      <div key={i} className="text-xs font-mono" style={{ color: '#8197A4' }}>{line}</div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Reorganize R2 into title-slug folders */}
        <div className="p-5 rounded-xl mb-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="reorganize-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: '#FFFFFF' }}>
                <RefreshCw size={20} color="#3B82F6" />
                Reorganize into title-slug folders
              </h3>
              <p className="text-sm mt-1" style={{ color: '#8197A4' }}>
                Rename R2 objects to use readable folders like <span className="font-mono text-xs" style={{ color: '#DDDDDD' }}>streamshelf/movies/melvyn-fd260d49/</span> instead of raw UUIDs — makes files easy to spot in the Cloudflare dashboard. Safe to re-run.
              </p>
            </div>
            <button onClick={startReorganize} disabled={reorganizing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold disabled:opacity-60"
              style={{ backgroundColor: '#3B82F6', color: 'white' }}
              data-testid="start-reorganize">
              {reorganizing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
              {reorganizing ? 'Running…' : 'Reorganize now'}
            </button>
          </div>
          {reorg && reorg.status !== 'idle' && (
            <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52' }}>
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: '#DDDDDD' }}>
                  {reorg.status === 'done'
                    ? <><CheckCircle2 className="inline mr-1" size={14} color="#10B981" /> Complete</>
                    : reorg.status === 'failed'
                    ? <><AlertTriangle className="inline mr-1" size={14} color="#EF4444" /> Failed</>
                    : <><Loader2 className="inline mr-1 animate-spin" size={14} color="#3B82F6" /> {reorg.status}</>}
                </span>
                <span style={{ color: '#8197A4' }}>
                  renamed {reorg.renamed || 0} · skipped {reorg.skipped || 0} · failed {reorg.failed || 0} / {reorg.total || 0}
                </span>
              </div>
              {reorg.total > 0 && (
                <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#2A3F52' }}>
                  <div className="h-full transition-all" style={{
                    width: `${Math.round(((reorg.renamed + reorg.skipped + reorg.failed) / reorg.total) * 100)}%`,
                    backgroundColor: '#3B82F6'
                  }} />
                </div>
              )}
              {reorg.errors && reorg.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer" style={{ color: '#EF4444' }}>{reorg.errors.length} error(s)</summary>
                  <div className="mt-2 space-y-1">
                    {reorg.errors.slice(0, 5).map((e, i) => (
                      <div key={i} className="text-xs" style={{ color: '#8197A4' }}>
                        <span style={{ color: '#EF4444' }}>{e.movie_id}/{e.field}:</span> {e.error}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Emergent Storage legacy files (post-migration originals) */}
        {legacy && legacy.object_count > 0 && (
          <div className="p-5 rounded-xl mb-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="emergent-legacy-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: '#FFFFFF' }}>
                  <HardDrive size={20} color="#8197A4" />
                  Legacy files on Emergent Storage
                </h3>
                <p className="text-sm mt-1" style={{ color: '#8197A4' }}>
                  These files were copied to Cloudflare R2 during migration but the originals remain on Emergent Storage. They are no longer served by your app — every asset URL now points at R2.
                </p>
                <div className="mt-2 text-sm" style={{ color: '#DDDDDD' }}>
                  <span style={{ color: '#F0C14B', fontWeight: 600 }}>{legacy.object_count}</span> objects · {legacy.total_size_human} total
                  {' · '}
                  <span style={{ color: '#EF4444', fontWeight: 600 }}>{legacy.orphaned_count}</span> orphaned ({legacy.orphaned_size_human})
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => setLegacyOpen(v => !v)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold"
                  style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3E5468' }}
                  data-testid="toggle-legacy-list">
                  {legacyOpen ? 'Hide list' : `Show ${legacy.object_count} files`}
                </button>
                <button onClick={deleteLegacy} disabled={legacyDeleting || !legacy.supported_delete}
                  className="px-3 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#EF4444', border: '1px solid #EF4444' }}
                  title={legacy.supported_delete ? 'Delete orphaned legacy files' : "Emergent Storage doesn't support DELETE yet — button will activate when it does"}
                  data-testid="delete-legacy-orphans">
                  {legacyDeleting ? <Loader2 className="inline animate-spin mr-1" size={14} /> : <Trash2 className="inline mr-1" size={14} />}
                  Delete orphaned
                </button>
              </div>
            </div>

            {!legacy.supported_delete && (
              <div className="mt-3 p-3 rounded-lg text-xs flex items-start gap-2"
                style={{ backgroundColor: 'rgba(240, 193, 75, 0.08)', border: '1px solid rgba(240, 193, 75, 0.35)', color: '#F0C14B' }}>
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Emergent Storage does not currently expose a DELETE API</strong> — the button above will return HTTP 405 today. Nothing is served from these files anymore (all references were rewritten to R2), so they only occupy Emergent&apos;s own quota, not yours. Contact Emergent support if you want them purged from the account.
                </div>
              </div>
            )}

            {legacyOpen && (
              <div className="mt-4 rounded-lg overflow-hidden max-h-96 overflow-y-auto" style={{ border: '1px solid #2A3F52' }}>
                {legacy.items.map((it, i) => (
                  <div key={it.path} className="p-2.5 flex items-center gap-3 text-xs"
                    style={{ borderTop: i === 0 ? 'none' : '1px solid #0F171E', backgroundColor: it.orphaned ? 'rgba(239,68,68,0.04)' : 'transparent' }}>
                    <span style={{ color: it.orphaned ? '#EF4444' : '#10B981' }}>
                      {it.orphaned ? '● orphan' : '● in use'}
                    </span>
                    <span className="font-mono truncate flex-1" style={{ color: '#DDDDDD' }} title={it.path}>{it.path}</span>
                    <span style={{ color: '#8197A4' }} className="flex-shrink-0">{fmtBytes(it.size)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Storage Stats */}
        {listing && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Total Storage', val: listing.total_size_human, color: '#3B82F6' },
              { label: 'Total Objects', val: listing.object_count, color: '#10B981' },
              { label: 'Movies with files', val: listing.movie_count, color: '#F0C14B' },
              { label: 'Orphaned groups', val: listing.orphaned_count, color: listing.orphaned_count > 0 ? '#EF4444' : '#8197A4' },
            ].map((s, i) => (
              <div key={i} className="p-4 rounded-lg" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                <div className="text-xs uppercase tracking-wider" style={{ color: '#8197A4' }}>{s.label}</div>
                <div className="text-2xl font-bold mt-1" style={{ color: s.color }}>{s.val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Groups by movie */}
        <div className="space-y-3" data-testid="storage-groups">
          {(listing?.groups || []).map((group) => {
            const isOpen = !!expanded[group.movie_id];
            return (
              <div key={group.movie_id} className="rounded-xl overflow-hidden"
                style={{ backgroundColor: '#1A2632', border: `1px solid ${group.is_orphaned ? '#EF4444' : '#2A3F52'}` }}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded(prev => ({ ...prev, [group.movie_id]: !isOpen }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(prev => ({ ...prev, [group.movie_id]: !isOpen })); } }}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-black/20 transition-all cursor-pointer"
                  data-testid={`storage-group-${group.movie_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {group.is_orphaned
                      ? <AlertTriangle size={20} color="#EF4444" />
                      : <Film size={20} color="#3B82F6" />}
                    <div className="min-w-0">
                      <div className="font-semibold truncate" style={{ color: '#FFFFFF' }}>{group.title}</div>
                      <div className="text-xs truncate" style={{ color: '#8197A4' }}>
                        {group.movie_id === '__other__' ? 'Non-movie files' : group.movie_id} · {group.items.length} files · {group.total_size_human}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {group.is_orphaned && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const folder = group.folder.endsWith('/') ? group.folder : group.folder + '/';
                          const inferredTitle = folder.replace(/\/$/, '').split('/').pop().replace(/-[a-f0-9]{8}$/, '').replace(/-/g, ' ');
                          restoreOrphan(folder, inferredTitle || group.title);
                        }}
                        disabled={busyKey === group.folder}
                        className="p-2 rounded-lg text-xs font-semibold hover:scale-105 transition-all"
                        style={{ backgroundColor: 'rgba(16,185,129,0.15)', color: '#10B981', border: '1px solid #10B981' }}
                        title="Restore these files as a new movie"
                        data-testid={`restore-orphan-${group.movie_id}`}
                      >
                        <ArrowUpCircle size={14} />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const firstKey = group.items[0]?.key || '';
                        const parts = firstKey.split('/');
                        const prefix = parts.slice(0, parts.length - 1).join('/') + '/';
                        deletePrefix(prefix, group.title);
                      }}
                      className="p-2 rounded-lg text-xs font-semibold hover:scale-105 transition-all"
                      style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#EF4444', border: '1px solid #EF4444' }}
                      title={`Delete all ${group.items.length} files for "${group.title}"`}
                      data-testid={`delete-group-${group.movie_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                    <span className="text-xl" style={{ color: '#8197A4' }}>{isOpen ? '−' : '+'}</span>
                  </div>
                </div>

                {isOpen && (
                  <div style={{ borderTop: '1px solid #2A3F52' }}>
                    {group.items.map((item) => {
                      const type = fileTypeLabel(item.key);
                      return (
                      <div key={item.key} className="p-3 flex items-center justify-between gap-3 flex-wrap"
                        style={{ borderTop: '1px solid #0F171E' }}>
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span style={{ color: type.color }}>{iconForKey(item.key)}</span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider flex-shrink-0"
                            style={{ backgroundColor: `${type.color}20`, color: type.color, border: `1px solid ${type.color}60` }}>
                            {type.label}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-mono truncate" style={{ color: '#DDDDDD' }} title={item.key}>
                              {item.key.split('/').slice(-2).join('/')}
                            </div>
                            <div className="text-xs" style={{ color: '#8197A4' }}>
                              {fmtBytes(item.size)} · {new Date(item.last_modified).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => downloadObject(item.key)}
                            disabled={busyKey === item.key}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold"
                            style={{ backgroundColor: 'rgba(59,130,246,0.15)', color: '#3B82F6', border: '1px solid #3B82F6' }}
                            data-testid={`download-${item.key}`}
                            title="Download">
                            <Download size={12} /> DL
                          </button>
                          <button onClick={() => deleteObject(item.key)}
                            disabled={busyKey === item.key}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold"
                            style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#EF4444', border: '1px solid #EF4444' }}
                            data-testid={`delete-${item.key}`}
                            title="Delete this file">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {(!listing?.groups || listing.groups.length === 0) && (
            <div className="text-center py-12 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
              <HardDrive size={48} color="#3E5468" className="mx-auto mb-3" />
              <div style={{ color: '#DDDDDD' }} className="font-semibold">No files in R2 yet</div>
              <div className="text-sm mt-1" style={{ color: '#8197A4' }}>Migrate existing files or upload a new movie to see them here</div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 mb-4" style={inputStyle}></div>
    </div>
  );
};

export default StorageManagerPage;
